import { Box, Stack, Tooltip } from '@mui/material';

import type { RoadmapNode } from "../../api/roadmap-service";
import type { LessonStatuses } from "../../utils/roadmap-progress";
import { stationProgress } from "../../utils/roadmap-progress";

import { choiceRule, pickAt } from "../../utils/roadmap-tree";

export type RoadmapChoiceStationProps = {
    node: RoadmapNode;
    level: number;
    statuses: LessonStatuses;
    levelName: (level: number) => string;
    fromLevel?: string;
    onOpen: (node: RoadmapNode) => void;
};

/**
 * A choice between alternatives, e.g. React, Angular or Vue.js: it says how many of them the selected belt expects,
 * and marks the ones the user already took up.
 */
export function RoadmapChoiceStation({ node, level, statuses, levelName, fromLevel, onOpen }: RoadmapChoiceStationProps) {
    const alternatives = node.children.length;
    // Above the belt nothing is expected yet, so the choice says what its first pick will ask for
    const count = fromLevel ? node.picks[0]?.count ?? 1 : pickAt(node, level);
    const rule = `${ choiceRule(count, alternatives) } · ${ node.title }`;
    const tooltip = node.picks.map((pick) => `${ levelName(pick.level) }: ${ choiceRule(pick.count, alternatives).toLowerCase() }`).join(' · ');

    return (
        <Stack data-testid="roadmap-choice" spacing={ 1 } sx={ {
            bgcolor: '#F5F0FF', border: '2px solid #8E5CD9', borderRadius: '14px', p: 1.25, opacity: fromLevel ? 0.4 : 1,
        } }>
            <Tooltip title={ tooltip }>
                <Box component="span" sx={ { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: '#6A3FC0', textTransform: 'uppercase' } }>
                    { rule }{ fromLevel ? ` · from ${ fromLevel }` : '' }
                </Box>
            </Tooltip>

            { node.children.map((option, index) => {
                const progress = stationProgress(option, statuses);
                const taken = progress.verified > 0;
                const note = option.planned ? 'planned' : progress.lessons ? `${ Math.round(progress.verified / progress.lessons * 100) }%` : '';

                return (
                    <Box key={ option.id ?? index } component="button" type="button" onClick={ () => onOpen(option) }
                         aria-label={ `${ option.title }${ taken ? ' — taken up' : '' }${ note ? ` — ${ note }` : '' }` }
                         sx={ {
                             display: 'flex', alignItems: 'center', gap: 1, p: 0, border: 0, bgcolor: 'transparent', font: 'inherit',
                             fontSize: 13, fontWeight: taken ? 800 : 600, color: option.planned ? '#637381' : '#161C24', cursor: 'pointer', textAlign: 'left',
                             '&:hover': { textDecoration: 'underline' },
                             '&:focus-visible': { outline: '2px solid #2065D1', outlineOffset: 2 },
                         } }>
                        <Box aria-hidden="true" sx={ {
                            width: 14, height: 14, borderRadius: '7px', flexShrink: 0, boxSizing: 'border-box',
                            border: `2px ${ option.planned ? 'dashed' : 'solid' } ${ taken ? '#8E5CD9' : '#919EAB' }`,
                            bgcolor: taken ? '#8E5CD9' : '#FFFFFF',
                        } }/>
                        { option.title }
                        <Box component="span" sx={ { ml: 'auto', fontSize: 11, color: '#4F5B67' } }>{ note }</Box>
                    </Box>
                );
            }) }
        </Stack>
    );
}
